// src/components/Navbar.js (Updated)
import React, { useState, useEffect, useRef, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LoginModal from "./LoginModal";
import WritersLoginModal from "./WritersLoginModal";
import { AuthContext } from "../context/AuthContext";
import "./Navbar.css";

const writingHelpLinks = [
  { name: "Essay", path: "/writing-help/essay" },
  { name: "Research Paper", path: "/writing-help/research-paper" },
  { name: "Thesis", path: "/writing-help/thesis" },
  { name: "Dissertation", path: "/writing-help/dissertation" },
  { name: "Assignment", path: "/writing-help/assignment" },
  { name: "Book Report", path: "/writing-help/book-report" },
  { name: "Speech", path: "/writing-help/speech" },
  { name: "Personal Statement", path: "/writing-help/personal-statement" },
  { name: "Literature Review", path: "/writing-help/literature-review" },
  { name: "Coursework", path: "/writing-help/coursework" },
  { name: "Lab Report", path: "/writing-help/lab-report" },
  { name: "Homework", path: "/writing-help/homework" },
  { name: "Case Study", path: "/writing-help/case-study" },
  { name: "Capstone Project", path: "/writing-help/capstone-project" },
  { name: "Plagiarism & AI-humanizer", path: "/writing-help/AI-humanizer" },
  { name: "Editing & Proofreading", path: "/writing-help/proofreading" },
  { name: "Referencing & Cititations", path: "/writing-help/Referencing" },
];

function Navbar() {
  const { isAuthenticated, user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isHovering, setIsHovering] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isWritersLoginModalOpen, setIsWritersLoginModalOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);
  const writingHelpRef = useRef(null);
  const userMenuRef = useRef(null);
  
  // Check if we're on the For Writers page
  const isForWritersPage = location.pathname === "/writers" || location.pathname.startsWith("/writers/");
  
  // Check if we're on the Order page
  const isOrderPage = location.pathname === "/order" || location.pathname.startsWith("/order/");
  
  // Check if we're on the writer-dashboard page
  const isWriterDashboard = location.pathname === "/writer-dashboard" || location.pathname.startsWith("/writer-dashboard/");
  
  // Check if we need simplified layout
  const isSimplifiedLayout = isForWritersPage || isOrderPage || isWriterDashboard;

  // Check if device is touch/mobile
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const checkTouchDevice = () => {
      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
      setIsMobileView(window.innerWidth <= 768);
    };
    
    checkTouchDevice();
    window.addEventListener('resize', checkTouchDevice);
    
    return () => {
      window.removeEventListener('resize', checkTouchDevice);
    };
  }, []);

  const toggleMobileDropdown = () => {
    if (isTouchDevice || isMobileView) {
      setIsMobileDropdownOpen(!isMobileDropdownOpen);
    }
  };

  const handleDropdownItemClick = () => {
    if (isTouchDevice || isMobileView) {
      setIsMobileDropdownOpen(false);
    }
  };

  const handleWritingHelpMouseEnter = () => {
    if (!isTouchDevice && !isMobileView) {
      setIsHovering(true);
    }
  };

  const handleWritingHelpMouseLeave = () => {
    if (!isTouchDevice && !isMobileView) {
      setIsHovering(false);
    }
  };

  const handleLoginClick = (e) => {
    e.preventDefault();
    if (isForWritersPage || isWriterDashboard) {
      // Show Writers Login Modal on For Writers or writer-dashboard pages
      setIsWritersLoginModalOpen(true);
    } else {
      // Show regular Client Login Modal on other pages
      setIsLoginModalOpen(true);
    }
    // Close mobile dropdown if open
    if (isMobileDropdownOpen) {
      setIsMobileDropdownOpen(false);
    }
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleDashboardClick = () => {
    if (user?.role === "ADMIN") {
      navigate("/admin-dashboard");
    } else if (user?.role === "WRITER") {
      navigate("/writer-dashboard");
    } else {
      navigate("/student-dashboard");
    }
    setIsUserMenuOpen(false);
  };

  const handleWriterDashboardClick = () => {
    navigate("/writer-dashboard");
    setIsUserMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    navigate("/");
  };

  // Close dropdown when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileDropdownOpen && 
          dropdownRef.current && 
          !dropdownRef.current.contains(event.target) &&
          writingHelpRef.current && 
          !writingHelpRef.current.contains(event.target)) {
        setIsMobileDropdownOpen(false);
      }
      
      if (isUserMenuOpen && 
          userMenuRef.current && 
          !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobileDropdownOpen, isUserMenuOpen]);

  // Prevent body scrolling when any modal is open
  useEffect(() => {
    if (isLoginModalOpen || isWritersLoginModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isLoginModalOpen, isWritersLoginModalOpen]);

  const getArrowDirection = () => {
    if (isMobileView || isTouchDevice) {
      return isMobileDropdownOpen ? "˄" : "˅";
    } else {
      return isHovering ? "˄" : "˅";
    }
  };

  const getUserInitial = () => {
    if (user?.username && user.username.length > 0) {
      return user.username.charAt(0).toUpperCase();
    }
    return "U";
  };

  // Determine button text based on page
  const getLoginButtonText = () => {
    if (isForWritersPage || isWriterDashboard) {
      return "Log In"; // Just "Log In" on For Writers or writer-dashboard pages
    }
    return "Log In / Sign Up"; // Original text on other pages
  };

  // Check if user is a writer (for writer dashboard)
  const isWriter = user?.role === "WRITER";

  return (
    <>
      <nav className={`navbar ${isSimplifiedLayout ? "simplified-layout" : ""} ${isForWritersPage ? "for-writers-page" : ""} ${isOrderPage ? "order-page" : ""} ${isWriterDashboard ? "writer-dashboard-page" : ""}`}>
        <div className="navbar-container">
          {/* LEFT - LOGO */}
          <Link to="/" className="navbar-logo">
            Research Writing
          </Link>

          {/* CENTER - MENU (hidden on simplified layout pages) */}
          {!isSimplifiedLayout && (
            <ul className="navbar-menu">
              <li 
                className={`navbar-item ${isMobileDropdownOpen ? "dropdown-container" : ""}`}
                ref={writingHelpRef}
                onMouseEnter={handleWritingHelpMouseEnter}
                onMouseLeave={handleWritingHelpMouseLeave}
                onClick={toggleMobileDropdown}
              >
                <span className="navbar-link">
                  Writing Help {getArrowDirection()}
                </span>

                <div 
                  ref={dropdownRef}
                  className={`dropdown-menu ${isMobileDropdownOpen ? "mobile-active" : ""}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {writingHelpLinks.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="dropdown-link"
                      onClick={handleDropdownItemClick}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </li>

              <li className="navbar-item">
                <Link to="/Samples" className="navbar-link">
                  Samples
                </Link>
              </li>

              <li className="navbar-item">
                <Link to="/blog" className="navbar-link">
                  Blog
                </Link>
              </li>

              <li className="navbar-item">
                <Link to="/writers" className="navbar-link">
                  For Writers
                </Link>
              </li>
            </ul>
          )}

          {/* RIGHT - ACTIONS */}
          <div className="navbar-actions">
            {isAuthenticated && user ? (
              // User is logged in - show avatar
              <>
                {/* Show avatar for all logged in users */}
                <div className="user-menu-container" ref={userMenuRef}>
                  <button 
                    onClick={toggleUserMenu}
                    className="user-avatar-btn"
                    aria-label="User menu"
                  >
                    <div className="user-avatar">
                      {getUserInitial()}
                    </div>
                    <span className="user-name">
                      {user.username}
                    </span>
                  </button>

                  <div className={`user-dropdown-menu ${isUserMenuOpen ? "active" : ""}`}>
                    <div className="user-info">
                      <div className="user-avatar-small">
                        {getUserInitial()}
                      </div>
                      <div className="user-details">
                        <div className="user-username">{user.username}</div>
                        <div className="user-email">{user?.email || "No email"}</div>
                        <div className="user-role">{user?.role || "USER"}</div>
                      </div>
                    </div>
                    
                    {/* Dashboard button - different for writers */}
                    {isWriter ? (
                      <button 
                        onClick={handleWriterDashboardClick}
                        className="user-dropdown-item"
                      >
                        Writer Dashboard
                      </button>
                    ) : (
                      <button 
                        onClick={handleDashboardClick}
                        className="user-dropdown-item"
                      >
                        Dashboard
                      </button>
                    )}
                    
                    <button 
                      onClick={handleLogout}
                      className="user-dropdown-item logout"
                    >
                      Log Out
                    </button>
                  </div>
                </div>
              </>
            ) : (
              // User is not logged in - show login button
              <>
                <button 
                  onClick={handleLoginClick}
                  className="navbar-login-button"
                  aria-label="Open login modal"
                >
                  {getLoginButtonText()}
                </button>
                
                {/* Hide Order Now on For Writers page, Order page, and writer-dashboard */}
                {!isSimplifiedLayout && !isForWritersPage && !isWriterDashboard && (
                  <Link to="/order" className="navbar-order">
                    Order Now
                  </Link>
                )}
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Client Login Modal */}
      <LoginModal 
        isOpen={isLoginModalOpen} 
        onClose={() => setIsLoginModalOpen(false)} 
      />
      
      {/* Writers Login Modal */}
      <WritersLoginModal 
        isOpen={isWritersLoginModalOpen} 
        onClose={() => setIsWritersLoginModalOpen(false)} 
      />
    </>
  );
}

export default Navbar;
